import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import ServiceSidebar from './ServiceSidebar';
import SetupBanner from './SetupBanner';
import Command from './command/Command';
import TopBar from './TopBar';
import { useAuth } from '../contexts/AuthContext';
import { useCommand } from '../context/CommandContext';

import { X } from '@phosphor-icons/react';

// Pages that should render full-bleed (no padding wrapper)
const FULL_BLEED_PATHS = [
  '/unified-agent-builder',
  '/partner-pro/ai-workforce/agents/create',
];

const AppShell = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, authReady } = useAuth();
  const location = useLocation();
  const { setWorkflowContext, clearEditingAgent } = useCommand();

  // Command pane state
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandInitialInput, setCommandInitialInput] = useState('');

  // Check if current page should be full-bleed
  const isFullBleed = FULL_BLEED_PATHS.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // Close Command and clear workflow context when navigating away from builder
  useEffect(() => {
    const isBuilderPage = location.pathname.includes('/automations/new') || location.pathname.includes('/workers/new') || location.pathname.includes('/workers/');
    if (!isBuilderPage) {
      setWorkflowContext(null);
      // Also clear any worker auto-binding so it doesn't leak into a fresh session
      clearEditingAgent();
      if (commandOpen) {
        setCommandOpen(false);
        setCommandInitialInput('');
      }
    }
  }, [location.pathname, setWorkflowContext, clearEditingAgent]);

  const [mountReady, setMountReady] = useState(false);

  useEffect(() => {
    if (authReady) {
      const timer = setTimeout(() => setMountReady(true), 50);
      return () => clearTimeout(timer);
    } else {
      setMountReady(false);
    }
  }, [authReady, user?.id, user?.role]);

  // Listen for open-command-drawer events (from sidebar, home page, Cmd+K)
  useEffect(() => {
    const handleOpen = (e) => {
      const inputText = e.detail?.input || '';
      const ctx = e.detail?.context === 'workflow' ? {
        workerName: e.detail.workerName,
        workerId: e.detail.workerId,
        automationType: e.detail.automationType,
        workerType: e.detail.workerType,
        steps: e.detail.steps,
      } : null;
      if (ctx) {
        setWorkflowContext(ctx);
        // The visible Process Flow on the right is the source of truth.
        // Clear any stale editingAgent binding from a prior session/auto-bind
        // so Command always targets the worker the user is actually looking at.
        clearEditingAgent();
      }
      setCommandInitialInput(inputText);
      setCommandOpen(true);
    };
    const handleToggle = () => {
      setCommandOpen(prev => {
        if (prev) { setCommandInitialInput(''); setWorkflowContext(null); }
        return !prev;
      });
    };
    const handleClose = () => {
      setCommandOpen(false);
      setCommandInitialInput('');
      setWorkflowContext(null);
    };

    window.addEventListener('open-command-drawer', handleOpen);
    window.addEventListener('toggle-command-drawer', handleToggle);
    window.addEventListener('close-command-drawer', handleClose);
    return () => {
      window.removeEventListener('open-command-drawer', handleOpen);
      window.removeEventListener('toggle-command-drawer', handleToggle);
      window.removeEventListener('close-command-drawer', handleClose);
    };
  }, []);

  // Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen(prev => {
          if (prev) setCommandInitialInput('');
          return !prev;
        });
      }
      if (e.key === 'Escape' && commandOpen) {
        e.preventDefault();
        setCommandOpen(false);
        setCommandInitialInput('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [commandOpen]);

  if (!authReady || !mountReady) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#121820]">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00E5C0]"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#121820]">
      {/* Desktop Sidebar — fixed-width spacer so content never shifts */}
      <div className="hidden lg:block h-full flex-shrink-0 w-[68px] relative z-[200]">
        <ServiceSidebar
          key={`sidebar-${user?.id || 'anonymous'}-${user?.role || 'norole'}-${authReady}`}
          commandOpen={commandOpen}
        />
      </div>

      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 z-50 lg:hidden"
            >
              <div className="relative h-full">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                >
                  <X size={20} className="text-white" />
                </button>
                <ServiceSidebar
                  key={`sidebar-mob-${user?.id || 'anonymous'}-${user?.role || 'norole'}-${authReady}`}
                  isMobile
                  onClose={() => setMobileMenuOpen(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* RIGHT MAIN CONTENT — Always visible */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* TopBar - Always on top, never hidden by Command */}
        {!isFullBleed && (
          <div className="relative z-[100] shrink-0">
            <TopBar onMenuClick={() => setMobileMenuOpen(true)} />
          </div>
        )}

        {/* Content area below header — Command + Page side by side */}
        <div className="flex-1 flex overflow-hidden min-w-0">
          {/* LEFT COMMAND PANE — inside content area, below header */}
          {commandOpen && (
            <div
              className="hidden lg:flex h-full flex-shrink-0 overflow-hidden transition-all duration-200"
              style={{ width: 'min(420px, 35vw)', minWidth: '340px' }}
              data-testid="command-split-pane"
            >
              <Command
                isOpen={commandOpen}
                onClose={() => { setCommandOpen(false); setCommandInitialInput(''); }}
                splitPane={true}
              />
            </div>
          )}

          {/* Page content */}
          <main
            className={`flex-1 overflow-y-auto relative z-[1] min-w-0 ${isFullBleed ? 'overflow-hidden' : ''}`}
            style={{ background: 'linear-gradient(180deg, #1A222C 0%, #121820 100%)', backgroundAttachment: 'fixed' }}
          >
            <SetupBanner />
            {isFullBleed ? (
              children
            ) : (
              <div className="p-6 md:p-8">
                {children}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
